import { useCallback, useState } from 'react';
import dayjs from 'dayjs';

import {
	pauseSubscription,
	SubscriptionActionTypes,
	useBusinessLayer,
	useDTicketSubscription,
} from '@mopla/business-logic';
import {
	calculateEditedPauseDates,
	TSubscriptionPauseDuration,
} from '@mopla/utils';

import { usePassengerAppNavigate } from '../../../../hooks';

export const useDTicketSubscriptionEditPause = () => {
	const { navigateToUserDTicket, navigateToSubscriptionEditPause } =
		usePassengerAppNavigate();
	const bl = useBusinessLayer();
	const { subscription, isSubscriptionLoading } = useDTicketSubscription();

	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
	const [isPauseLoading, setIsPauseLoading] = useState(false);

	const handleEditPause = useCallback(
		(duration: TSubscriptionPauseDuration) => {
			if (
				!subscription?.id ||
				!subscription?.pauseStart ||
				!subscription?.pauseEnd
			)
				return;

			const dates = calculateEditedPauseDates(
				parseInt(duration, 10),
				subscription.pauseStart,
				subscription.pauseEnd
			);

			setIsPauseLoading(true);

			const pauseStartFormatted = dayjs(dates.pauseStart, 'DD.MM.YYYY').format(
				'YYYY-MM-DD'
			);

			const newPauseEndFormatted = dayjs(dates.activeFrom, 'DD.MM.YYYY')
				.subtract(1, 'day')
				.format('YYYY-MM-DD');

			bl.dispatch(
				pauseSubscription({
					subscriptionId: subscription.id,
					pauseStart: pauseStartFormatted,
					pauseEnd: newPauseEndFormatted,
				})
			);

			bl.watchActions({
				types: SubscriptionActionTypes.PauseSubscriptionResult,
				callback: (action) => {
					setIsPauseLoading(false);

					if (action.payload?.error) {
						console.error('Error pausing subscription:', action.payload.error);
						setIsErrorModalOpen(true);
					} else {
						setIsSuccessModalOpen(true);
					}
				},
			});
		},
		[subscription?.id, subscription?.pauseStart, subscription?.pauseEnd, bl]
	);

	const closeSuccessModal = useCallback(() => {
		setIsSuccessModalOpen(false);
		navigateToUserDTicket();
	}, [navigateToUserDTicket]);

	const retryPause = useCallback(() => {
		setIsErrorModalOpen(false);
		navigateToSubscriptionEditPause();
	}, [navigateToSubscriptionEditPause]);

	return {
		isSubscriptionLoading,
		isSuccessModalOpen,
		isErrorModalOpen,
		isPauseLoading,
		handleEditPause,
		closeSuccessModal,
		retryPause,
	};
};
